/**
 * AgentSupportCard
 *
 * One card, four states of the agent support add-on (BR-100..BR-105):
 *   Off / eligible   price line + "Turn on agent support"
 *   On               success chip, "$X/month on your bill", "Turn off"
 *   Ending           warning chip "Ends <date>", "Agents keep working until <date>…", "Keep agent support"
 *   Not eligible     secondary chip "Paid plans" + "Choose a plan" (trial) — or the reason
 * While an accepted change (turn on / off / keep) has not shown up in the read yet the card says
 * "Updating…" and offers no action — it never shows the stale state as if it were current.
 * Not offered on the plan at all (`agentUnitPriceCents: null`, outside a trial) -> not rendered.
 */

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { AgentSupportPoints } from './AgentSupportPoints';
import { formatUsd, formatPeriodDate, type SubscriptionStatus } from '../../api/billing';

interface AgentSupportCardProps {
  subscription: SubscriptionStatus;
  onTurnOn: () => void;
  onTurnOff: () => void;
  onKeep: () => void;
  keepPending: boolean;
  /** An accepted add-on change the billing subsystem has not applied yet (see BillingPage). */
  updating?: boolean;
}

const NOT_ACTIVE_COPY = 'Agent support can be turned on once your subscription is active and up to date.';

export function AgentSupportCard({ subscription, onTurnOn, onTurnOff, onKeep, keepPending, updating = false }: AgentSupportCardProps) {
  const { agentAddon, agentUnitPriceCents, unitCount, nextBillingDate } = subscription;
  const isTrial = agentAddon.ineligibleReason === 'trial';

  // Plans that don't offer (or already include) the add-on never show the card; a trial does,
  // so the landlord learns agents come with a paid plan.
  if (!isTrial && (agentUnitPriceCents === null || agentAddon.ineligibleReason === 'zero_price_bundle')) {
    return null;
  }
  if (agentAddon.ineligibleReason === 'not_offered') return null;

  const monthlyCents = (agentUnitPriceCents ?? 0) * unitCount;
  const endsOn = formatPeriodDate(nextBillingDate);

  let chip: React.ReactNode;
  let detail: React.ReactNode;
  let action: React.ReactNode;

  if (updating) {
    chip = (
      <Badge variant="secondary" data-testid="agent-support-status">
        Updating…
      </Badge>
    );
    detail = (
      <p role="status" className="m-0 text-sm text-muted-foreground" data-testid="agent-support-detail">
        Applying your change — this usually takes a few seconds.
      </p>
    );
    action = null;
  } else if (agentAddon.status === 'cancelling') {
    chip = (
      <Badge variant="warning" data-testid="agent-support-status">
        Ends {endsOn}
      </Badge>
    );
    detail = (
      <p className="m-0 text-sm text-muted-foreground" data-testid="agent-support-detail">
        Agents keep working until {endsOn}. No agent charge after that.
      </p>
    );
    action = (
      <Button
        type="button"
        variant="outline"
        className="w-full md:w-auto min-h-[44px]"
        data-testid="agent-support-keep"
        disabled={keepPending}
        onClick={onKeep}
      >
        {keepPending ? 'Keeping…' : 'Keep agent support'}
      </Button>
    );
  } else if (agentAddon.status === 'active') {
    chip = (
      <Badge variant="success" data-testid="agent-support-status">
        On
      </Badge>
    );
    detail = (
      <p className="m-0 text-sm text-muted-foreground" data-testid="agent-support-detail">
        {formatUsd(monthlyCents)}/month on your bill
      </p>
    );
    action = (
      <Button
        type="button"
        variant="outline"
        className="w-full md:w-auto min-h-[44px]"
        data-testid="agent-support-turn-off"
        onClick={onTurnOff}
      >
        Turn off
      </Button>
    );
  } else if (isTrial) {
    chip = (
      <Badge variant="secondary" data-testid="agent-support-status">
        Paid plans
      </Badge>
    );
    detail = (
      <p className="m-0 text-sm text-muted-foreground" data-testid="agent-support-detail">
        Available once you choose a plan. Your trial includes listings, not agents.
      </p>
    );
    action = (
      <Button
        type="button"
        variant="link"
        className="min-h-[44px] px-0"
        data-testid="agent-support-choose-plan"
        onClick={() => document.getElementById('available-plans')?.scrollIntoView({ behavior: 'smooth' })}
      >
        Choose a plan
      </Button>
    );
  } else {
    const canTurnOn = agentAddon.eligible;
    chip = (
      <Badge variant="secondary" data-testid="agent-support-status">
        Off
      </Badge>
    );
    detail = (
      <>
        <AgentSupportPoints testId="agent-support-points" />
        <p className="m-0 text-sm text-foreground" data-testid="agent-support-price">
          <strong>{formatUsd(agentUnitPriceCents ?? 0)}</strong> per unit / month
          <span className="text-muted-foreground">
            {' '}
            · {unitCount} unit{unitCount === 1 ? '' : 's'} = {formatUsd(monthlyCents)}/month
          </span>
        </p>
        {!canTurnOn && (
          <p className="m-0 text-sm text-muted-foreground" data-testid="agent-support-detail">
            {NOT_ACTIVE_COPY}
          </p>
        )}
      </>
    );
    action = (
      <Button
        type="button"
        className="w-full md:w-auto min-h-[44px]"
        data-testid="agent-support-turn-on"
        disabled={!canTurnOn}
        onClick={onTurnOn}
      >
        Turn on agent support
      </Button>
    );
  }

  return (
    <section
      className="bg-card rounded-lg border border-border p-5 mb-6 flex flex-col gap-3"
      aria-labelledby="agent-support-heading"
      data-testid="agent-support-card"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 id="agent-support-heading" className="text-lg font-semibold text-foreground m-0">
          Agent support
        </h2>
        {chip}
      </div>
      {detail}
      <div>{action}</div>
    </section>
  );
}
